"use client";

import { useState, useEffect, useRef, useContext, useMemo } from "react";
import { MessageCircle, Video, ArrowRight, X } from "lucide-react";
import { useRouter } from "next/navigation";

import styles from "./PricingCard.module.scss";
import CallButton from "./components/CallButton";
import { AppContext, chatMemberProfile } from "@/context/AppContext";
import { startCall } from "@/services/call.service";
import { CurrencyType } from "@/types/common.types";
import { ToastType } from "@/utils/common.types";
import { ProfileInfoDataInterface } from "@/modules/Astro/UserProfile/ProfilesInfo/profileTypes";

interface PricingOption {
  id: string;
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  subtitle: string;
  price: string;
  buttonText: string;
  slotsLeftText: string;
}

interface PricingCardProps {
  userData?: ProfileInfoDataInterface;
}

export default function PricingCard({ userData }: PricingCardProps) {
  const router = useRouter();
  const context = useContext(AppContext);
  const { token } = context?.auth ?? {};
  const {
    setPopups,
    zegoClient,
    privateCallSocket,
    setPrivateCallData = () => null,
    userData: callUser,
    auth,
    setToastData,
    walletBalance,
    userData: currUserData,
    chatData,
    setChatData,
  } = context ?? {};

  const profile = userData?.profile;

  const [consultingCount] = useState(() => Math.floor(Math.random() * 101) + 100);
  const [chatSlots] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [videoSlots] = useState(() => Math.floor(Math.random() * 10) + 1);

  const options = useMemo<PricingOption[]>(() => {
    if (!profile) return [];
    const result: PricingOption[] = [];

    if (profile.permissions?.receivePaidDM) {
      result.push({
        id: "chat",
        icon: <MessageCircle size={22} color="#f5931b" />,
        iconColor: "#f5931b",
        title: "Chat",
        subtitle: "Per minute billing",
        price: `₹${profile.pricePerMinuteChatInInr}/min`,
        buttonText: "Start Chat",
        slotsLeftText: `Only ${chatSlots} chat slots left today`,
      });
    }

    if (profile.permissions?.receivePrivateCall) {
      result.push({
        id: "video",
        icon: <Video size={22} color="#0b5cff" />,
        iconColor: "#0b5cff",
        title: "Video Call",
        subtitle: "Face-to-face session",
        price: `₹${profile.pricePerMinuteInInr}/min`,
        buttonText: "Start Video Call",
        slotsLeftText: `Only ${videoSlots} video slots left today`,
      });
    }

    return result;
  }, [profile, chatSlots, videoSlots]);

  const [selectedId, setSelectedId] = useState<string>("");
  const [showSheet, setShowSheet] = useState(false);
  const [cardVisible, setCardVisible] = useState(false);
  const cardRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (options.length > 0 && !options.find((o) => o.id === selectedId)) {
      setSelectedId(options[0].id);
    }
  }, [options]);

  const selectedOption =
    options.find((opt) => opt.id === selectedId) ?? options[0];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setCardVisible(entry.isIntersecting),
      { threshold: 0.4 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = showSheet ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showSheet]);

  const handleChatClick = () => {
    if (!token) {
      setPopups?.({ showLoginPopup: true });
      return;
    }
    if (
      (currUserData?.diamonds ?? 0) <=
        (profile?.pricePerMinuteChatInInr ?? 0) &&
      (walletBalance?.astroVCard ?? 0) === 0
    ) {
      setChatData?.({ ...chatData, isInsufficientBalancePopup: true });
      return;
    }
    if (profile && setChatData) {
      setChatData({
        initiator: {
          attributes: { isOnline: true },
          user: currUserData as unknown as chatMemberProfile,
        },
        receiver: {
          attributes: {},
          user: {
            photo: profile.profilePic,
            name: profile.name,
            userId: profile.userId,
            pricePerMinuteChatInInr: profile.pricePerMinuteChatInInr,
          },
        },
        currencyType:
          (walletBalance?.astroVCard ?? 0) > 0
            ? CurrencyType.ASTROVCARDS
            : CurrencyType.DIAMONDS,
      });
      void router.push(`/chat-intake-form?receiverUserId=${profile.userId}`);
    }
  };

  const handleCallClick = () => {
    if (!token) {
      setPopups?.({ showLoginPopup: true });
      return;
    }
    if (!profile) return;

    if (
      (currUserData?.diamonds ?? 0) <= (profile.pricePerMinuteInInr ?? 0) &&
      (walletBalance?.astroVCard ?? 0) === 0
    ) {
      setPrivateCallData({ insufficientBalancePopup: true });
      return;
    }

    if (!zegoClient || !privateCallSocket) {
      setToastData?.({
        message: "Call service is not ready. Please try again.",
        type: ToastType.ERROR,
        visible: true,
      });
      return;
    }

    if (!callUser || !auth?.userId) {
      setToastData?.({
        message: "Loading user data. Please try again.",
        type: ToastType.ERROR,
        visible: true,
      });
      return;
    }

    const callerUser = { ...profile, photo: profile.profilePic };
    void startCall(
      zegoClient,
      callerUser,
      () => {
        setPrivateCallData({
          callPopup: true,
          caller: {
            ...callUser,
            photo: callUser?.profilePic,
            userId: auth?.userId ?? "",
          },
          contentOwner: callerUser,
        });
      },
      setToastData,
      privateCallSocket,
      setPrivateCallData
    );
  };

  const handlePrimaryAction = () => {
    if (!selectedOption) return;
    if (selectedOption.id === "chat") {
      handleChatClick();
    } else {
      handleCallClick();
    }
  };

  if (options.length === 0) return null;

  return (
    <>
      {/* ─── Desktop / Tablet: full pricing card ─── */}
      <aside ref={cardRef} className={styles.card} id="pricing-options">
        <div className={styles.header}>
          <span className={styles.online}>
            <span className={styles.dot} />
            {"Available now"}
          </span>
        </div>

        <div className={styles.options}>
          {options.map((item) => (
            <div
              key={item.id}
              onClick={() => setSelectedId(item.id)}
              className={`${styles.option} ${
                item.id === selectedId ? styles.active : ""
              }`}
            >
              <div className={styles.left}>
                <div
                  className={styles.icon}
                  style={{
                    boxShadow: `0 0 0 1.5px ${item.iconColor}50`,
                    backgroundColor: `${item.iconColor}12`,
                  }}
                >
                  {item.icon}
                </div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.subtitle}</p>
                </div>
              </div>
              <span className={styles.price}>{item.price}</span>
            </div>
          ))}
        </div>

        <CallButton
          fullWidth
          icon={<ArrowRight size={22} />}
          onClick={handlePrimaryAction}
        >
          {selectedOption?.buttonText}
        </CallButton>

        <div className={styles.footer}>
          <div className={styles.footerItem}>
            <span className={`${styles.statusDot} ${styles.green}`} />
            <p>{consultingCount} people consulting right now</p>
          </div>
          {options.map((opt) => (
            <div key={opt.id} className={styles.footerItem}>
              <span className={`${styles.statusDot} ${styles.yellow}`} />
              <p>{opt.slotsLeftText}</p>
            </div>
          ))}
        </div>
      </aside>

      {/* ─── Mobile: sticky bottom bar — hidden when pricing card is visible ─── */}
      <div
        className={`${styles.stickyBottomBar} ${
          cardVisible ? styles.stickyHidden : ""
        }`}
      >
        <div className={styles.stickyLeft}>
          <span className={styles.stickyPrice}>{selectedOption?.price}</span>
          <span className={styles.stickyDetail}>
            {selectedOption?.title} · {selectedOption?.subtitle}
          </span>
        </div>

        <div className={styles.stickyRight}>
          <button
            className={styles.stickyButton}
            onClick={() => setShowSheet(true)}
          >
            {selectedOption?.id === "chat" ? "Start Chat" : "Start Call"}
          </button>
        </div>
      </div>

      {/* ─── Bottom sheet — opens only from sticky footer ─── */}
      {showSheet && (
        <div className={styles.backdrop} onClick={() => setShowSheet(false)}>
          <div
            className={styles.bottomSheet}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.sheetHeader}>
              <h3>Select Mode</h3>
              <button
                className={styles.closeButton}
                onClick={() => setShowSheet(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles.sheetContent}>
              <div className={styles.selectorPanel}>
                {options.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => setSelectedId(item.id)}
                    className={`${styles.selectorOption} ${
                      item.id === selectedId ? styles.selectorActive : ""
                    }`}
                  >
                    <div className={styles.left}>
                      <div
                        className={styles.icon}
                        style={{
                          boxShadow: `0 0 0 1.5px ${item.iconColor}50`,
                          backgroundColor: `${item.iconColor}12`,
                        }}
                      >
                        {item.icon}
                      </div>
                      <div>
                        <h4>{item.title}</h4>
                        <p>{item.subtitle}</p>
                      </div>
                    </div>
                    <span className={styles.price}>{item.price}</span>
                  </div>
                ))}
              </div>

              <div className={styles.sheetStats}>
                <div className={styles.footerItem}>
                  <span className={`${styles.statusDot} ${styles.green}`} />
                  <p>{consultingCount} people consulting right now</p>
                </div>
                {options.map((opt) => (
                  <div key={opt.id} className={styles.footerItem}>
                    <span className={`${styles.statusDot} ${styles.yellow}`} />
                    <p>{opt.slotsLeftText}</p>
                  </div>
                ))}
              </div>

              <div className={styles.sheetFooter}>
                <CallButton
                  fullWidth
                  icon={<ArrowRight size={22} />}
                  onClick={() => {
                    setShowSheet(false);
                    handlePrimaryAction();
                  }}
                >
                  {selectedOption?.buttonText}
                </CallButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
